import {
  DEFAULT_AUDIO_SETTINGS,
  createDefaultSettingsStore,
  type AudioSettings,
  type SettingsStore,
} from '@/systems/SettingsStore';
import { createDefaultSfxPlayer, type SfxName, type SfxOptions, type SfxPlayer } from '@/systems/SfxPlayer';

/**
 * AudioManager — orquestra configurações de áudio e dispara SFX.
 *
 * Decisões:
 *
 * 1. **Singleton** (instância exportada `audioManager`) para que qualquer
 *    cena chame `audioManager.playSfx('correct')` sem injeção de dep.
 *    Em testes, criar instâncias próprias com mocks.
 *
 * 2. **Lazy SFX**: o `SfxPlayer` (e portanto o AudioContext) só é criado
 *    quando `init()` é chamado, dentro de uma cena Phaser — depois do
 *    layout do DOM. Evita warnings de autoplay em SSR/headless.
 *
 * 3. **Autoplay policy**: a primeira chamada a `playSfx` tenta destravar
 *    o AudioContext via `unlock()`. Como playSfx é sempre disparado por
 *    callbacks de UI (clique/toque), o navegador autoriza o resume.
 *
 * 4. **Música**: a API existe (`playMusic` / `stopMusic`) mas o player
 *    interno é placeholder até termos arquivos `.ogg` reais. Hoje é
 *    no-op silencioso — qualquer cena pode chamar sem se preocupar.
 */

export interface AudioManagerOptions {
  /** Sobrescreve a store de configurações (testes). */
  store?: SettingsStore;
  /** Fábrica de SFX player (testes). */
  sfxFactory?: () => SfxPlayer;
}

export class AudioManager {
  private settings: AudioSettings = { ...DEFAULT_AUDIO_SETTINGS };
  private sfx: SfxPlayer | null = null;
  private initialized = false;
  private readonly store: SettingsStore;
  private readonly sfxFactory: () => SfxPlayer;

  constructor(options: AudioManagerOptions = {}) {
    this.store = options.store ?? createDefaultSettingsStore();
    this.sfxFactory = options.sfxFactory ?? createDefaultSfxPlayer;
  }

  /**
   * Inicializa: carrega settings, instancia o SFX player e aplica volume.
   * Idempotente — chamadas repetidas são no-ops.
   */
  init(): void {
    if (this.initialized) return;
    this.settings = this.store.load() ?? { ...DEFAULT_AUDIO_SETTINGS };
    this.sfx = this.sfxFactory();
    this.applyVolume();
    this.initialized = true;
  }

  getSettings(): AudioSettings {
    return { ...this.settings };
  }

  setSfxVolume(volume: number): void {
    this.settings.sfxVolume = clamp01(volume);
    this.applyVolume();
    this.persist();
  }

  setMusicVolume(volume: number): void {
    this.settings.musicVolume = clamp01(volume);
    this.persist();
  }

  setVoiceVolume(volume: number): void {
    this.settings.voiceVolume = clamp01(volume);
    this.persist();
  }

  setMuted(muted: boolean): void {
    this.settings.muted = muted;
    this.applyVolume();
    this.persist();
  }

  /** Inverte o estado de mute. Devolve o novo estado para a UI atualizar. */
  toggleMuted(): boolean {
    this.setMuted(!this.settings.muted);
    return this.settings.muted;
  }

  isMuted(): boolean {
    return this.settings.muted;
  }

  /** Toca um SFX. No-op se mudo, volume zerado ou ainda não inicializado. */
  playSfx(name: SfxName, options?: SfxOptions): void {
    if (!this.initialized || this.settings.muted || this.settings.sfxVolume <= 0) return;
    this.sfx?.play(name, options);
  }

  /**
   * Atalho: chamar no primeiro gesto do usuário para destravar o
   * AudioContext (alguns navegadores exigem isso explicitamente).
   */
  unlock(): void {
    void this.sfx?.unlock();
  }

  /* ----------------------- música (placeholder) ----------------------- */

  playMusic(_trackId: string): void {
    // Placeholder: música real entra quando tivermos arquivos em
    // public/assets/audio/music. Por enquanto é no-op silencioso.
    void _trackId;
  }

  stopMusic(): void {
    // Placeholder.
  }

  /* ------------------------------ internos ------------------------------ */

  private applyVolume(): void {
    if (!this.sfx) return;
    const effective = this.settings.muted ? 0 : this.settings.sfxVolume;
    this.sfx.setVolume(effective);
  }

  private persist(): void {
    this.store.save({ ...this.settings });
  }
}

function clamp01(v: number): number {
  if (Number.isNaN(v)) return 0;
  return Math.max(0, Math.min(1, v));
}

/**
 * Singleton compartilhado entre cenas. BootScene chama `init()`; depois
 * disso qualquer cena pode invocar `audioManager.playSfx(...)`.
 *
 * Para testes, NÃO usar este singleton — criar `new AudioManager(...)`
 * com mocks via `AudioManagerOptions`.
 */
export const audioManager = new AudioManager();
